import React from 'react';
import * as THREE from 'three';

import vertexShader from './vertex-shader.glsl';
import fragmentShader from './fragment-shader.glsl';
import { modes } from '../../statemachine';

const CLEAR_COLOR = new THREE.Color('#000000');

// We do this as a class because threejs is super stateful.
class VisualisationScene {
    canvas = null;
    audioRecorder = null;
    orbState = modes.inactive;
    animationFrame = null;

    // THREE stuff
    scene = null;
    renderer = null;
    width = null;
    height = null;
    camera = null;
    clock = null;

    // Node stuff
    material = null;
    mesh = null;
    geometry = null;

    // State
    smoothedVolume = 0;
    targetShapeState = 0;
    targetColourState = 0;
    currentShapeState = 0;
    currentColourState = 0;

    constructor(_canvas) {
        console.log('construct');
        this.canvas = _canvas;

        this.update = this.update.bind(this);

        this.initThree();
    }

    initThree() {
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas });

        const canvasSize = this.canvas.getBoundingClientRect();

        this.width = canvasSize.width;
        this.height = canvasSize.height;

        this.renderer.setSize(this.width, this.height);
        this.renderer.setClearColor(CLEAR_COLOR);

        this.camera = new THREE.OrthographicCamera(
            this.width / -2.6,
            this.width / 2.6,
            this.height / 2.6,
            this.height / -2.6,
            1,
            1000
        );
        //this.camera.position.y = -200;
        this.camera.position.z = -200;
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));

        this.clock = new THREE.Clock();

        this.initScene();

        // Start the loop
        this.animationFrame = window.requestAnimationFrame(this.update);
    }

    initScene() {
        this.targetColourState = this.orbStateToColourState();
        this.targetShapeState = this.orbStateToShapeState();
        this.currentColourState = this.targetColourState;
        this.currentShapeState = this.targetShapeState;

        console.log('INIT', this.targetColourState, this.targetShapeState);

        this.geometry = new THREE.BufferGeometry();

        const numPoints = 50000;
        let vertices = new Float32Array(numPoints * 3);
        for (let i = 0; i < numPoints; i++) {
            const index = i / numPoints;
            vertices[i * 3] = index;
        }
        this.geometry.addAttribute(
            'position',
            new THREE.BufferAttribute(vertices, 3)
        );

        this.material = new THREE.ShaderMaterial({
            uniforms: {
                volume: { value: 0 },
                time: { value: 0 },
                shapeState: { value: this.currentShapeState },
                colourState: { value: this.currentColourState },
            },
            defaultAttributeValues: {},
            side: THREE.DoubleSide,
            opacity: true,
            transparent: true,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            blending: THREE.AdditiveBlending,
        });

        this.mesh = new THREE.Points(this.geometry, this.material);

        this.scene.add(this.mesh);
    }

    setAudioRecorder(_recorder) {
        this.audioRecorder = _recorder;
    }

    destroy() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
    }

    getVolumeValue() {
        if (this.audioRecorder && this.audioRecorder.lastBuffer) {
            const sum = this.audioRecorder.lastBuffer.reduce(
                (acc, n) => acc + Math.abs(n),
                0
            );
            const meanVal = sum / this.audioRecorder.lastBuffer.length;
            return meanVal;
        }
        return 0;
    }

    update() {
        const dt = this.clock.getDelta();
        const elapsed = this.clock.elapsedTime;

        this.setVolume();
        this.updateStateTransitions(dt);

        // Handle time update
        this.material.uniforms.time.value = elapsed;
        this.material.uniforms.time.needsUpdate = true;

        this.renderer.render(this.scene, this.camera);

        // Looping
        this.animationFrame = window.requestAnimationFrame(this.update);
    }

    setVolume() {
        // Handle volume uniform
        const newVolume = this.getVolumeValue();

        const diff = newVolume - this.smoothedVolume;
        let applyDiff = Math.min(0.1, diff * 0.20);
        if (Math.abs(applyDiff) < 0.0005) {
            this.smoothedVolume = newVolume;
        } else {
            this.smoothedVolume += applyDiff;
        }

        this.material.uniforms.volume.value = this.smoothedVolume;
        this.material.uniforms.volume.needsUpdate = true;
    }

    orbStateToShapeState() {
        switch (this.orbState) {
            case modes.orbHidden:
            case modes.active:
            case modes.inactive:
            default:
                // Circle
                return 0;
            case modes.userSpeaking:
            case modes.userAudioPlayback:
                // Ring shape
                return 1;
        }
    }

    orbStateToColourState() {
        switch (this.orbState) {
            // White
            case modes.orbHidden:
            case modes.inactive:
            default:
                return 0;
            // Lavendar
            case modes.userAudioPlayback:
                return 1;
            case modes.active:
            case modes.userSpeaking:
                // Purple
                return 2;
        }
    }

    setOrbState(_orbState) {
        console.log(_orbState);
        this.orbState = _orbState;
        this.targetShapeState = this.orbStateToShapeState();
        this.targetColourState = this.orbStateToColourState();
    }

    updateStateTransitions(dt) {
        if (this.currentShapeState !== this.targetShapeState) {
            const dir = this.targetShapeState > this.currentShapeState ? 1 : -1;
            this.currentShapeState += 2.5 * dt * dir;
            if (
                (dir === 1 && this.currentShapeState > this.targetShapeState) ||
                (dir === -1 && this.currentShapeState < this.targetShapeState)
            ) {
                this.currentShapeState = this.targetShapeState;
            }

            this.material.uniforms.shapeState.value = this.currentShapeState;
            this.material.uniforms.shapeState.needsUpdate = true;
        }
        if (this.currentColourState !== this.targetColourState) {
            const dir =
                this.targetColourState > this.currentColourState ? 1 : -1;
            this.currentColourState += 2.5 * dt * dir;

            if (
                (dir === 1 &&
                    this.currentColourState > this.targetColourState) ||
                (dir === -1 && this.currentColourState < this.targetColourState)
            ) {
                this.currentColourState = this.targetColourState;
            }
            this.material.uniforms.colourState.value = this.currentColourState;
            this.material.uniforms.colourState.needsUpdate = true;
        }
    }
}

function Orb(props) {
    const { orbstate, audioRecorder } = props;

    const canvasRef = React.useRef(null);
    const visScene = React.useRef(null);
    React.useEffect(() => {
        if (canvasRef.current) {
            visScene.current = new VisualisationScene(canvasRef.current);
        }
        return () => {
            visScene.current && visScene.current.destroy();
            visScene.current = null;
        };
    }, [canvasRef]);

    React.useEffect(() => {
        if (visScene.current) {
            visScene.current.setAudioRecorder(audioRecorder);
        }
        return () => {
            visScene.current && visScene.current.setAudioRecorder(null);
        };
    }, [audioRecorder]);

    React.useEffect(() => {
        if (visScene.current) {
            visScene.current.setOrbState(orbstate);
        }
    }, [orbstate, visScene]);

    return (
        <div
            style={{
                height: '100%',
                width: '100%',
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    height: '120%',
                    width: '120%',
                }}
            />
        </div>
    );
}

export default Orb;
